import { useState, useEffect } from 'react'
import { api } from '../lib/api.js'
import { formatRelativeDate } from '../lib/dates.js'
import ConfirmDialog from '../components/ConfirmDialog.jsx'

const PRIO_OPTIONS = [
  { value: 1, label: 'Hoch', color: 'var(--color-danger, #ef4444)' },
  { value: 2, label: 'Mittel', color: 'var(--color-warning, #f59e0b)' },
  { value: 3, label: 'Normal', color: 'var(--color-text-muted)' },
]

const STATUS_FILTERS = [
  { value: 'open', label: 'Offen' },
  { value: 'done', label: 'Erledigt' },
  { value: 'all', label: 'Alle' },
]

export default function Dashboard() {
  const [tasks, setTasks] = useState([])
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState(() => {
    try {
      const saved = localStorage.getItem('pendenzen_filter')
      if (saved) return JSON.parse(saved)
    } catch {}
    return { status: 'open', project_id: '' }
  })
  const [fadingOut, setFadingOut] = useState(new Set())
  const [followups, setFollowups] = useState([])
  const [confirmDeleteId, setConfirmDeleteId] = useState(null)
  const [expandedDescs, setExpandedDescs] = useState(new Set())

  // New task form
  const [showForm, setShowForm] = useState(false)
  const [newTask, setNewTask] = useState({ title: '', description: '', due_date: '', priority: 3, project_id: '' })
  const [saving, setSaving] = useState(false)

  // Inline editing
  const [editingId, setEditingId] = useState(null)
  const [editData, setEditData] = useState({})
  const [editSaving, setEditSaving] = useState(false)

  // Load tasks
  async function loadTasks() {
    try {
      const params = {}
      if (filter.status !== 'all') params.status = filter.status
      if (filter.project_id) params.project_id = filter.project_id
      const result = await api.tasks(params)
      setTasks(result)
    } catch (err) {
      console.error('[Pendenzen] Laden fehlgeschlagen:', err)
    }
  }

  // Load projects for filter/dropdown
  useEffect(() => {
    api.dashboard().then(data => {
      if (data.projects) setProjects(data.projects)
    }).catch(() => {})
    // Vorgänge auf 'Nachfassen' — Hinweis-Banner oben
    api.vorgaenge({ status: 'followup' }).then(setFollowups).catch(() => {})
  }, [])

  useEffect(() => {
    localStorage.setItem('pendenzen_filter', JSON.stringify(filter))
    setLoading(true)
    loadTasks().finally(() => setLoading(false))
  }, [filter.status, filter.project_id])

  // --- Actions ---
  async function markDone(task) {
    setFadingOut(prev => new Set(prev).add(task.id))
    try {
      await api.updateTask(task.id, { status: 'done' })
      setTimeout(() => {
        setTasks(prev => prev.filter(t => t.id !== task.id))
        setFadingOut(prev => { const n = new Set(prev); n.delete(task.id); return n })
      }, 350)
    } catch (err) {
      console.error('Task-Update fehlgeschlagen:', err)
      setFadingOut(prev => { const n = new Set(prev); n.delete(task.id); return n })
    }
  }

  async function reopenTask(task) {
    try {
      await api.updateTask(task.id, { status: 'open' })
      setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: 'open' } : t))
    } catch (err) {
      console.error('Task-Reopen fehlgeschlagen:', err)
    }
  }

  async function deleteTask(id) {
    setConfirmDeleteId(null)
    setFadingOut(prev => new Set(prev).add(id))
    try {
      await api.deleteTask(id)
      setTimeout(() => {
        setTasks(prev => prev.filter(t => t.id !== id))
        setFadingOut(prev => { const n = new Set(prev); n.delete(id); return n })
      }, 350)
    } catch (err) {
      console.error('Task-Loeschen fehlgeschlagen:', err)
      setFadingOut(prev => { const n = new Set(prev); n.delete(id); return n })
    }
  }

  async function createTask() {
    if (!newTask.title.trim()) return
    setSaving(true)
    try {
      const payload = {
        title: newTask.title.trim(),
        description: newTask.description.trim() || null,
        due_date: newTask.due_date || null,
        priority: newTask.priority,
        project_id: newTask.project_id ? Number(newTask.project_id) : null,
        status: 'open',
      }
      const result = await api.createTask(payload)
      const projectName = newTask.project_id ? projects.find(p => p.id === Number(newTask.project_id))?.name : null
      setTasks(prev => [{ id: result.id, ...payload, project_name: projectName || null }, ...prev])
      setNewTask({ title: '', description: '', due_date: '', priority: 3, project_id: '' })
      setShowForm(false)
    } catch (err) {
      console.error('Task erstellen fehlgeschlagen:', err)
    } finally {
      setSaving(false)
    }
  }

  function startEdit(task) {
    setEditingId(task.id)
    setEditData({
      title: task.title || '',
      description: task.description || '',
      due_date: task.due_date || '',
      priority: task.priority || 3,
      project_id: task.project_id || '',
    })
  }

  function cancelEdit() {
    setEditingId(null)
    setEditData({})
  }

  async function saveEdit() {
    if (!editData.title.trim()) return
    setEditSaving(true)
    try {
      const payload = {
        title: editData.title.trim(),
        description: editData.description.trim() || null,
        due_date: editData.due_date || null,
        priority: editData.priority,
        project_id: editData.project_id ? Number(editData.project_id) : null,
      }
      await api.updateTask(editingId, payload)
      const projectName = editData.project_id ? projects.find(p => p.id === Number(editData.project_id))?.name : null
      setTasks(prev => prev.map(t => t.id === editingId ? { ...t, ...payload, project_name: projectName || null } : t))
      setEditingId(null)
      setEditData({})
    } catch (err) {
      console.error('Task-Update fehlgeschlagen:', err)
    } finally {
      setEditSaving(false)
    }
  }

  function toggleDesc(id) {
    setExpandedDescs(prev => {
      const n = new Set(prev)
      n.has(id) ? n.delete(id) : n.add(id)
      return n
    })
  }

  // --- Group tasks by due date ---
  function groupTasks(taskList) {
    const overdue = []
    const today = []
    const thisWeek = []
    const later = []
    const noDue = []

    const now = new Date()
    const todayStr = now.toISOString().split('T')[0]
    const weekEnd = new Date(now)
    weekEnd.setDate(weekEnd.getDate() + (7 - weekEnd.getDay()))
    const weekEndStr = weekEnd.toISOString().split('T')[0]

    for (const t of taskList) {
      if (!t.due_date) { noDue.push(t); continue }
      if (t.due_date < todayStr) overdue.push(t)
      else if (t.due_date === todayStr) today.push(t)
      else if (t.due_date <= weekEndStr) thisWeek.push(t)
      else later.push(t)
    }

    const groups = []
    if (overdue.length) groups.push({ label: 'Ueberfaellig', tasks: overdue, color: 'var(--color-danger, #ef4444)' })
    if (today.length) groups.push({ label: 'Heute', tasks: today, color: 'var(--color-primary)' })
    if (thisWeek.length) groups.push({ label: 'Diese Woche', tasks: thisWeek, color: 'var(--color-text-secondary)' })
    if (later.length) groups.push({ label: 'Spaeter', tasks: later, color: 'var(--color-text-muted)' })
    if (noDue.length) groups.push({ label: 'Ohne Datum', tasks: noDue, color: 'var(--color-text-muted)' })
    return groups
  }

  const groups = filter.status === 'open' ? groupTasks(tasks) : [{ label: '', tasks, color: 'var(--color-text-muted)' }]
  const totalCount = tasks.length

  const inputStyle = {
    padding: '8px 10px', borderRadius: 6, fontSize: 13,
    border: '1px solid var(--color-border)', background: 'var(--color-bg)',
    color: 'var(--color-text)', outline: 'none', fontFamily: 'inherit',
  }

  return (
    <div className="fade-in" style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 0 12px', flexShrink: 0 }}>
        <div>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text-muted)' }}>{totalCount} {filter.status === 'open' ? 'offen' : filter.status === 'done' ? 'erledigt' : 'gesamt'}</p>
        </div>
        <button
          onClick={() => setShowForm(v => !v)}
          style={{
            padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
            background: showForm ? 'var(--color-text-muted)' : 'var(--color-primary)',
            color: '#fff', fontSize: 13, fontWeight: 600,
            display: 'flex', alignItems: 'center', gap: 6,
            transition: 'background 0.15s',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Neue Pendenz
        </button>
      </div>

      {/* Vorgänge mit Funkstille — nachfassen */}
      {followups.length > 0 && (
        <div
          onClick={() => { window.location.hash = '#/vorgaenge' }}
          title="Zum Vorgänge-Modul"
          style={{
            padding: '10px 14px', marginBottom: 12, borderRadius: 8, flexShrink: 0, cursor: 'pointer',
            border: '1px solid var(--color-primary)', background: 'var(--color-bg)',
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-primary)', marginBottom: 4 }}>
            {followups.length} {followups.length === 1 ? 'Vorgang wartet' : 'Vorgänge warten'} auf Nachfassen
          </div>
          {followups.slice(0, 4).map(v => (
            <div key={v.id} style={{ fontSize: 12, color: 'var(--color-text-secondary)', padding: '1px 0' }}>
              {v.title}{v.days_waiting > 0 ? ` — seit ${v.days_waiting} Tagen keine Rückmeldung` : ''}
            </div>
          ))}
          {followups.length > 4 && (
            <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 2 }}>… und {followups.length - 4} weitere</div>
          )}
        </div>
      )}

      {/* New task form */}
      {showForm && (
        <div style={{
          padding: 16, marginBottom: 12, borderRadius: 8, flexShrink: 0,
          border: '1px solid var(--color-border)', background: 'var(--color-bg)',
        }}>
          <input
            type="text"
            placeholder="Was muss erledigt werden?"
            value={newTask.title}
            onChange={e => setNewTask(prev => ({ ...prev, title: e.target.value }))}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); createTask() } if (e.key === 'Escape') setShowForm(false) }}
            autoFocus
            style={{ ...inputStyle, width: '100%', fontSize: 14, fontWeight: 500 }}
          />
          <textarea
            placeholder="Beschreibung (optional)"
            value={newTask.description}
            onChange={e => setNewTask(prev => ({ ...prev, description: e.target.value }))}
            rows={2}
            style={{ ...inputStyle, width: '100%', marginTop: 8, resize: 'vertical' }}
          />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' }}>
            <select
              value={newTask.project_id}
              onChange={e => setNewTask(prev => ({ ...prev, project_id: e.target.value }))}
              style={{ ...inputStyle, minWidth: 160 }}
            >
              <option value="">Kein Projekt</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.project_number} {p.name}</option>)}
            </select>
            <input
              type="date"
              value={newTask.due_date}
              onChange={e => setNewTask(prev => ({ ...prev, due_date: e.target.value }))}
              style={inputStyle}
            />
            <select
              value={newTask.priority}
              onChange={e => setNewTask(prev => ({ ...prev, priority: Number(e.target.value) }))}
              style={inputStyle}
            >
              {PRIO_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            <div style={{ flex: 1 }} />
            <button
              onClick={() => { setShowForm(false); setNewTask({ title: '', description: '', due_date: '', priority: 3, project_id: '' }) }}
              style={{ ...inputStyle, cursor: 'pointer', background: 'transparent' }}
            >Abbrechen</button>
            <button
              onClick={createTask}
              disabled={!newTask.title.trim() || saving}
              style={{
                padding: '8px 18px', borderRadius: 6, fontSize: 13, cursor: 'pointer',
                border: 'none', background: 'var(--color-primary)', color: '#fff',
                fontWeight: 600, opacity: !newTask.title.trim() || saving ? 0.5 : 1,
              }}
            >{saving ? 'Speichern...' : 'Erstellen'}</button>
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexShrink: 0, alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 2, background: 'var(--color-bg)', borderRadius: 6, border: '1px solid var(--color-border)', overflow: 'hidden' }}>
          {STATUS_FILTERS.map(sf => (
            <button
              key={sf.value}
              onClick={() => setFilter(prev => ({ ...prev, status: sf.value }))}
              style={{
                padding: '6px 14px', fontSize: 12, border: 'none', cursor: 'pointer',
                background: filter.status === sf.value ? 'var(--color-primary)' : 'transparent',
                color: filter.status === sf.value ? '#fff' : 'var(--color-text-muted)',
                fontWeight: filter.status === sf.value ? 600 : 400,
                transition: 'all 0.15s',
              }}
            >{sf.label}</button>
          ))}
        </div>
        <select
          value={filter.project_id}
          onChange={e => setFilter(prev => ({ ...prev, project_id: e.target.value }))}
          style={{
            padding: '6px 10px', borderRadius: 6, fontSize: 12,
            border: '1px solid var(--color-border)', background: 'var(--color-bg)',
            color: 'var(--color-text)', outline: 'none',
          }}
        >
          <option value="">Alle Projekte</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.project_number} {p.name}</option>)}
        </select>
      </div>

      {/* Task list */}
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {loading ? (
          <div className="loading-center"><div className="spinner" /></div>
        ) : tasks.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--color-text-muted)' }}>
            <p style={{ fontSize: 14 }}>Keine Pendenzen{filter.status === 'open' ? ' offen' : ''}.</p>
          </div>
        ) : (
          groups.map((group, gi) => (
            <div key={gi} style={{ marginBottom: group.label ? 16 : 0 }}>
              {group.label && (
                <div style={{
                  fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
                  color: group.color, padding: '4px 0 6px', borderBottom: '1px solid var(--color-border-light)',
                  marginBottom: 4,
                }}>
                  {group.label} ({group.tasks.length})
                </div>
              )}
              {group.tasks.map(task => {
                const isFading = fadingOut.has(task.id)
                const prio = PRIO_OPTIONS.find(p => p.value === task.priority)
                const isDone = task.status === 'done'
                const isExpanded = expandedDescs.has(task.id)
                const descLong = task.description && task.description.length > 120
                const isEditing = editingId === task.id

                if (isEditing) {
                  return (
                    <div key={task.id} style={{
                      padding: '12px 4px', borderBottom: '1px solid var(--color-border-light)',
                      display: 'flex', flexDirection: 'column', gap: 8,
                    }}>
                      <input
                        type="text"
                        value={editData.title}
                        onChange={e => setEditData(prev => ({ ...prev, title: e.target.value }))}
                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveEdit() } if (e.key === 'Escape') cancelEdit() }}
                        autoFocus
                        style={{ ...inputStyle, width: '100%', fontSize: 14, fontWeight: 500 }}
                      />
                      <textarea
                        value={editData.description}
                        onChange={e => setEditData(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Beschreibung (optional)"
                        rows={2}
                        style={{ ...inputStyle, width: '100%', resize: 'vertical' }}
                      />
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <select
                          value={editData.project_id}
                          onChange={e => setEditData(prev => ({ ...prev, project_id: e.target.value }))}
                          style={{ ...inputStyle, minWidth: 160 }}
                        >
                          <option value="">Kein Projekt</option>
                          {projects.map(p => <option key={p.id} value={p.id}>{p.project_number} {p.name}</option>)}
                        </select>
                        <input
                          type="date"
                          value={editData.due_date}
                          onChange={e => setEditData(prev => ({ ...prev, due_date: e.target.value }))}
                          style={inputStyle}
                        />
                        <select
                          value={editData.priority}
                          onChange={e => setEditData(prev => ({ ...prev, priority: Number(e.target.value) }))}
                          style={inputStyle}
                        >
                          {PRIO_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                        </select>
                        <div style={{ flex: 1 }} />
                        <button onClick={cancelEdit} style={{ ...inputStyle, cursor: 'pointer', background: 'transparent' }}>Abbrechen</button>
                        <button
                          onClick={saveEdit}
                          disabled={!editData.title.trim() || editSaving}
                          style={{
                            padding: '8px 18px', borderRadius: 6, fontSize: 13, cursor: 'pointer',
                            border: 'none', background: 'var(--color-primary)', color: '#fff',
                            fontWeight: 600, opacity: !editData.title.trim() || editSaving ? 0.5 : 1,
                          }}
                        >{editSaving ? 'Speichern...' : 'Speichern'}</button>
                      </div>
                    </div>
                  )
                }

                return (
                  <div
                    key={task.id}
                    className="task-item"
                    onClick={() => startEdit(task)}
                    style={{
                      opacity: isFading ? 0 : 1,
                      maxHeight: isFading ? 0 : 500,
                      overflow: 'hidden',
                      transition: 'opacity 0.3s, max-height 0.3s ease-out',
                      padding: isFading ? '0' : '10px 4px',
                      alignItems: 'flex-start',
                      borderBottom: '1px solid var(--color-border-light)',
                      cursor: 'pointer',
                    }}
                  >
                    {/* Checkbox */}
                    <span
                      onClick={(e) => { e.stopPropagation(); isDone ? reopenTask(task) : markDone(task) }}
                      style={{
                        width: 18, height: 18, flexShrink: 0, borderRadius: 4, cursor: 'pointer',
                        border: isDone ? 'none' : '1.5px solid var(--color-border)',
                        background: isDone ? 'var(--color-success, #22c55e)' : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        marginTop: 2, transition: 'all 0.2s',
                      }}
                      onMouseEnter={(e) => { if (!isDone) { e.currentTarget.style.borderColor = 'var(--color-success, #22c55e)'; e.currentTarget.style.background = 'rgba(34,197,94,0.1)' } }}
                      onMouseLeave={(e) => { if (!isDone) { e.currentTarget.style.borderColor = 'var(--color-border)'; e.currentTarget.style.background = 'transparent' } }}
                    >
                      {isDone && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                    </span>

                    {/* Content */}
                    <div className="flex-1" style={{ minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span style={{
                          fontSize: 13, fontWeight: 500,
                          textDecoration: isDone ? 'line-through' : 'none',
                          color: isDone ? 'var(--color-text-muted)' : 'var(--color-text)',
                        }}>{task.title}</span>
                        {prio && prio.value <= 2 && (
                          <span style={{
                            fontSize: 10, padding: '1px 5px', borderRadius: 3, flexShrink: 0,
                            background: prio.color + '18', color: prio.color, fontWeight: 600,
                          }}>{prio.label}</span>
                        )}
                      </div>
                      {task.description && (
                        <div
                          style={{
                            fontSize: 12, color: 'var(--color-text-muted)', marginTop: 2,
                            ...(!isExpanded && descLong ? {
                              display: '-webkit-box', WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical', overflow: 'hidden',
                            } : {}),
                          }}
                        >{task.description}</div>
                      )}
                      {task.project_name && (
                        <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 2, display: 'inline-block' }}>
                          {task.project_name}
                        </span>
                      )}
                    </div>

                    {/* Right side: date + delete */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                      {task.due_date && (
                        <span style={{
                          fontSize: 11, padding: '2px 8px', borderRadius: 4,
                          background: (!isDone && task.due_date < new Date().toISOString().split('T')[0])
                            ? 'rgba(239,68,68,0.1)' : 'var(--color-bg-secondary, rgba(0,0,0,0.04))',
                          color: (!isDone && task.due_date < new Date().toISOString().split('T')[0])
                            ? 'var(--color-danger, #ef4444)' : 'var(--color-text-muted)',
                          fontWeight: 500,
                        }}>
                          {formatRelativeDate(task.due_date)}
                        </span>
                      )}
                      <span
                        onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(task.id) }}
                        style={{
                          color: 'var(--color-text-muted)', cursor: 'pointer',
                          opacity: 0, transition: 'opacity 0.15s, color 0.15s',
                          padding: '2px 4px', lineHeight: 1, display: 'flex',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.8'; e.currentTarget.style.color = 'var(--color-danger, #ef4444)' }}
                        onMouseLeave={(e) => { e.currentTarget.style.opacity = '0'; e.currentTarget.style.color = 'var(--color-text-muted)' }}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      </span>
                    </div>
                  </div>
                )
              })}
            </div>
          ))
        )}
      </div>

      {/* Confirm delete dialog */}
      {confirmDeleteId && (
        <ConfirmDialog
          title="Pendenz loeschen"
          message="Diese Pendenz wird unwiderruflich geloescht."
          confirmLabel="Loeschen"
          danger
          onConfirm={() => deleteTask(confirmDeleteId)}
          onCancel={() => setConfirmDeleteId(null)}
        />
      )}
    </div>
  )
}
